import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useContext } from 'react';

import { suppressCostEstimatesForPolicies } from '../../../..//utils/featureSuppress';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { CostIndicator } from '../../../../common/CostIndicator';
import { InNetworkBadgeWithDate } from '../../../../common/InNetworkBadge/InNetworkBadge';
import { PSXIcon } from '../../../../common/PSXIcon';
import { PortalContext } from '../../../../context/PortalContext';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../hooks/useSmartChoice';
import { isProviderStartDateInFuture } from '../../../../utils/date.utils';
import { capitalizeFirstLetterOfEachWord } from '../../../../utils/providerNames.utils';
import { getShowCost } from '../../../../utils/showCost.utils';
import {
  CostIndicatorFlex,
  DividerStyle,
  IconAndText,
  LayoutGroupStyled,
} from './CompareSmartChoice.styled';
import { Tier2Icon } from './Tier2Icon';
import { isAttributeChecked } from './utils';

export const replacePlaceholders = (
  text: string | undefined,
  replacements: Record<string, string>
): string =>
  (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
    const keys = key.split('.');
    let value: any = replacements;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    return typeof value === 'string' ? value : '';
  });

export const Benefits = ({
  displayInNetworkBadgeEnabled,
  isTier2Plan,
  selectedAttributes,
  selectedProviders,
  filteredProviders,
}) => {
  const isSmartChoice = useSmartChoice();
  const lagoonData = useLagoon('ui-messaging')();
  const coverageType = useCoverageType();
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);
  const [costEnableFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
  ]);

  const displayInNetworkBadge = (provider) =>
    isProviderStartDateInFuture(provider?.networkStartDate);

  const acoNameDisplay = (provider) =>
    provider?.acoProvider?.acoNetworkDescription?.join(', ') ?? '';

  const isProviderTierDesignation = (provider) =>
    provider?.recommendationDetails?.benefits?.providerTierDesignation?.toLowerCase();

  const isCostEfficiencyRating = (provider) =>
    provider?.recommendationDetails?.benefits?.costEfficiencyRating;

  const in_network_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_IN_NETWORK,
  })?.message;
  const out_network_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_OUT_OF_NETWORK,
  })?.message;
  const provider_tier = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER,
  })?.message;
  const not_provider_tier = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_TIERED_PROVIDER,
  })?.message;
  const provider_tier_2 = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_TIER_2_PROVIDER,
  })?.message;
  const preferred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER,
  })?.message;
  const not_preferred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_PREFERRED_PROVIDER,
  })?.message;
  const aco_provider_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ACO_PROVIDER_TEXT,
  })?.message;
  const not_aco_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_ACO_PROVIDER,
  })?.message;
  const compare_below_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_BELOW_AVERAGE_COST,
  })?.message;
  const compare_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_AVERAGE_COST,
  })?.message;
  const compare_above_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ABOVE_AVERAGE_COST,
  })?.message;
  const compare_cost_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_COST_NOT_AVAILABLE,
  })?.message;
  const compare_below_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_BELOW_AVERAGE_COST_CARE,
  })?.message;
  const compare_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_AVERAGE_COST_CARE,
  })?.message;
  const compare_above_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ABOVE_AVERAGE_COST_CARE,
  })?.message;
  const compare_cost_care_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_COST_CARE_NOT_AVAILABLE,
  })?.message;

  const acoText = (provider) => {
    const acoName: string = capitalizeFirstLetterOfEachWord(
      acoNameDisplay(provider)
    );
    const location = `${provider?.address?.line}, ${provider?.address?.city}, ${provider?.address?.state}`;
    return replacePlaceholders(aco_provider_text, { location, acoName });
  };
  const isAcoAvailable = selectedProviders?.some(
    (item) => item?.acoProvider?.acoNetworkDescription?.length > 0
  );

  const costEfficiencyRatingCodes = ['A', 'B', 'C', 'D'];

  const suppressCostEstimates = suppressCostEstimatesForPolicies(
    coverageType,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES_EMPIRE
  );

  return (
    <React.Fragment>
      {(selectedAttributes?.find((item) => item?.name === 'networkStatus')
        ?.isChecked ||
        !isSmartChoice) && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="compare-provider-status"
                key={index}
              >
                {displayInNetworkBadge(provider) &&
                displayInNetworkBadgeEnabled ? (
                  <Text
                    data-auto-testid={`compare-card-network-status-${provider?.providerId}`}
                    data-testid={`compare-card-network-status-${provider?.providerId}`}
                  >
                    <InNetworkBadgeWithDate
                      message={in_network_provider}
                      startDate={provider?.networkStartDate}
                    />
                  </Text>
                ) : (
                  <React.Fragment>
                    <IconSymbol color="#007000" icon="done" size={20} />
                    <Text
                      css={{ marginLeft: '8px' }}
                      data-auto-testid={`compare-card-network-status-${provider?.providerId}`}
                      data-testid={`compare-card-network-status-${provider?.providerId}`}
                    >
                      {provider?.networkStatus
                        ? in_network_provider
                        : out_network_provider}
                    </Text>
                  </React.Fragment>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isSmartChoice &&
            isAttributeChecked(selectedAttributes, [
              'tier',
              'tierOneProvider',
              'tierOneAndTwoProvider',
              'greenDiamond',
              'acoAffiliation',
              'costIndicator',
              'cost',
              'costEfficiency',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          {!isSmartChoice &&
            isAttributeChecked(selectedAttributes, [
              'acceptingNewPatients',
              'hoursOfOperation',
              'virtualCareOffered',
              'distance',
              'hasWeekendAppointment',
              'hasEveningAppointments',
              'cost',
              'tierOneProvider',
              'tierOneAndTwoProvider',
              'acoAffiliation',
              'hoursOfOperation',
              'hasEveningAppointments',
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'gender',
              'boardCertified',
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
              'appointmentAvailability',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
        </>
      )}
      {selectedAttributes?.find((item) => item?.name === 'tier')?.isChecked &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled data-testid="provider-tier" key={index}>
                  {isProviderTierDesignation(provider) === 'y' ? (
                    <IconAndText>
                      <PSXIcon icon="Group 204976975" size={24} />
                      <Text>{provider_tier}</Text>
                    </IconAndText>
                  ) : (
                    not_provider_tier
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'greenDiamond',
              'acoAffiliation',
              'costIndicator',
              'cost',
              'costEfficiency',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find(
        (item) =>
          item?.name === 'tierOneProvider' ||
          item?.name === 'tierOneAndTwoProvider'
      )?.isChecked &&
        !isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((item, index) => (
                <LayoutGroupStyled data-testid="provider-tier" key={index}>
                  {item?.isTieredProvider ? (
                    <IconAndText>
                      <PSXIcon icon="Group 204976975" size={24} />
                      <Text>{provider_tier}</Text>
                    </IconAndText>
                  ) : item?.isTier2Provider && isTier2Plan ? (
                    <IconAndText>
                      <Tier2Icon />
                      <Text>{provider_tier_2}</Text>
                    </IconAndText>
                  ) : (
                    not_provider_tier
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'acceptingNewPatients',
              'hoursOfOperation',
              'virtualCareOffered',
              'distance',
              'cost',
              'acoAffiliation',
              'hoursOfOperation',
              'hasEveningAppointments',
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'gender',
              'boardCertified',
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
              'appointmentAvailability',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'greenDiamond')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                data-testid="provider-preferred-provider"
                key={index}
              >
                {provider?.isGreenDiamondProvider ? (
                  <IconAndText>
                    <PSXIcon icon={'green_diamond'} size={24} />
                    <Text>{preferred_provider}</Text>
                  </IconAndText>
                ) : (
                  <Text>{not_preferred_provider}</Text>
                )}
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>

          {isAttributeChecked(selectedAttributes, [
            'acoAffiliation',
            'costIndicator',
            'cost',
            'costEfficiency',
            'acoAffiliation',
            'hoursOfOperation',
            'hasEveningAppointments',
            'hasWeekendAppointment',
            'onlineAppointmentScheduling',
            'virtualCareOffered',
            'distance',
            'languageSpoken',
            'languagesSpoken',
            'gender',
            'boardCertified',
            'reviews',
            'providerEthnicityExpress',
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
            'appointmentAvailability',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find((item) => item?.name === 'acoAffiliation')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {filteredProviders?.map((provider, index) => (
              <LayoutGroupStyled
                css={{ minHeight: isAcoAvailable ? '40px' : '20px' }}
                data-testid="provider-aco-name"
                key={index}
              >
                <Text>
                  {acoNameDisplay(provider) !== ''
                    ? acoText(provider)
                    : not_aco_provider}
                </Text>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          {isAttributeChecked(selectedAttributes, [
            'costIndicator',
            'cost',
            'costEfficiency',
            'hoursOfOperation',
            'hasEveningAppointments',
            'hasWeekendAppointment',
            'onlineAppointmentScheduling',
            'virtualCareOffered',
            'distance',
            'languageSpoken',
            'languagesSpoken',
            'gender',
            'boardCertified',
            'reviews',
            'providerEthnicityExpress',
            'eapAccepted',
            'practiceLimitations',
            'servicesOffered',
            'appointmentAvailability',
          ]) && (
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {filteredProviders?.map((_, index) => (
                <React.Fragment key={index}>
                  <DividerStyle />
                </React.Fragment>
              ))}
            </LayoutGroupStyled>
          )}
        </>
      )}
      {selectedAttributes?.find((item) => item?.name === 'costIndicator')
        ?.isChecked &&
        portalShowCost &&
        costEnableFlag &&
        !suppressCostEstimates &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  css={{ alignItems: 'center !important', minHeight: '50px' }}
                  data-testid="provider-cost-indicator"
                  key={index}
                >
                  {provider?.costIndicator ? (
                    <CostIndicatorFlex>
                      <CostIndicator indicatorData={provider?.costIndicator} />
                      {provider?.costIndicator === '$' && (
                        <Text color="$success1">
                          {compare_below_average_cost}
                        </Text>
                      )}
                      {provider?.costIndicator === '$$' && (
                        <Text color="$primary1">{compare_average_cost}</Text>
                      )}
                      {provider?.costIndicator === '$$$' && (
                        <Text color="$error1">
                          {compare_above_average_cost}
                        </Text>
                      )}
                    </CostIndicatorFlex>
                  ) : (
                    <Text>{compare_cost_unavailable}</Text>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            {isAttributeChecked(selectedAttributes, ['costEfficiency']) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'cost')?.isChecked &&
        portalShowCost &&
        costEnableFlag &&
        !suppressCostEstimates &&
        !isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  css={{ alignItems: 'center !important', minHeight: '50px' }}
                  data-testid="provider-cost-indicator"
                  key={index}
                >
                  {provider?.costIndicator ? (
                    <CostIndicatorFlex>
                      <CostIndicator indicatorData={provider?.costIndicator} />
                      {provider?.costIndicator === '$' && (
                        <Text color="$success1">
                          {compare_below_average_cost}
                        </Text>
                      )}
                      {provider?.costIndicator === '$$' && (
                        <Text color="$primary1">{compare_average_cost}</Text>
                      )}
                      {provider?.costIndicator === '$$$' && (
                        <Text color="$error1">
                          {compare_above_average_cost}
                        </Text>
                      )}
                    </CostIndicatorFlex>
                  ) : (
                    <Text>{compare_cost_unavailable}</Text>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>

            {isAttributeChecked(selectedAttributes, [
              'hoursOfOperation',
              'hasEveningAppointments',
              'hasWeekendAppointment',
              'onlineAppointmentScheduling',
              'virtualCareOffered',
              'distance',
              'languageSpoken',
              'languagesSpoken',
              'gender',
              'boardCertified',
              'reviews',
              'providerEthnicityExpress',
              'eapAccepted',
              'practiceLimitations',
              'servicesOffered',
              'appointmentAvailability',
            ]) && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {filteredProviders?.map((_, index) => (
                  <React.Fragment key={index}>
                    <DividerStyle />
                  </React.Fragment>
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'costEfficiency')
        ?.isChecked &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {filteredProviders?.map((provider, index) => (
                <LayoutGroupStyled
                  css={{ alignItems: 'center !important' }}
                  data-testid="provider-cost-efficiency"
                  key={index}
                >
                  {costEfficiencyRatingCodes.includes(
                    isCostEfficiencyRating(provider)
                  ) && <Text>{compare_below_average_cost_care}</Text>}
                  {isCostEfficiencyRating(provider) === 'E' && (
                    <Text>{compare_average_cost_care}</Text>
                  )}
                  {['F', 'G'].includes(isCostEfficiencyRating(provider)) && (
                    <Text>{compare_above_average_cost_care}</Text>
                  )}
                  {isCostEfficiencyRating(provider) === '' && (
                    <Text>{compare_cost_care_unavailable}</Text>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
          </>
        )}
    </React.Fragment>
  );
};
